import React, { useMemo, useState } from "react";
import { Modal, Tag } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { GoPencil } from "react-icons/go";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { useStatusTrackerV2, useStatusTrackerModal } from "../hooks/useStatusTrackerV2";
import { selectCurrentCells } from "../state/selectors";
import { monthKeyOf } from "../state/utils";
import { buildCellViewModel } from "../mappers/cellViewModel.mapper";
import { buildGPSCriteriaList } from "../mappers/gpsCriteria.mapper";
import { DATA_SOURCE } from "../state/actions";
import { AcwrTooltip } from "../views/tooltips/AcwrTooltip";
import { FlagTooltip } from "../views/tooltips/FlagTooltip";
import { InjuryTooltip } from "../views/tooltips/InjuryTooltip";
import { GpsFlagChecklist } from "../views/tooltips/GpsFlagChecklist";
import { StatusUpdater } from "./StatusUpdater";

// One shared modal at the grid root (not one per cell). Reads the selected cell from modal context.
export const CellDetailModal = () => {
    const { selectedCellKey, closeCell } = useStatusTrackerModal();
    const { state, team, actions } = useStatusTrackerV2();
    const [editingStatus, setEditingStatus] = useState(null);
    const [customInputVisible, setCustomInputVisible] = useState(false);

    const statuses = state.statuses;
    const [playerId, dateStr] = selectedCellKey ? selectedCellKey.split("_") : [];
    const player = team.players.find((p) => p.player_id === Number(playerId));
    const cellInfo = selectedCellKey ? selectCurrentCells(state)[selectedCellKey] : null;

    // Only team-owned (custom) statuses are editable/deletable — built-in/global ones are managed
    // centrally (the server also enforces this: edit/delete of a global type → 404). The v2
    // status-types list omits ownership, but each assigned cell's status carries `type`, so derive
    // it from the month's cells (and prefer an explicit `type` on the status object when present).
    const statusTypeById = useMemo(() => {
        const map = {};
        for (const c of Object.values(selectCurrentCells(state))) {
            const s = c?.status;
            const sid = s?.status_id ?? s?.id;
            if (sid != null && s?.type != null) map[sid] = s.type;
        }
        return map;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.byMonth, state.filters.selectedMonth]);

    const isCustomStatus = (status) => {
        const sid = status?.id ?? status?.status_id;
        return (status?.type ?? statusTypeById[sid]) === "custom";
    };

    // V1 restricts edit/delete to the status CREATOR (and only custom types): see backend
    // StatusTracker.py (edit_allowed/delete_allowed = type=="custom" && user==creator) and the
    // StatusTrackerViews delete guard. When the v2 list carries those booleans, honour them verbatim;
    // until then fall back to custom-only (the strongest signal the current v2 payload allows).
    const canEdit = (status) =>
        typeof status?.edit_allowed === "boolean" ? status.edit_allowed : isCustomStatus(status);
    const canDelete = (status) =>
        typeof status?.delete_allowed === "boolean" ? status.delete_allowed : isCustomStatus(status);

    // Context bag the status-type CRUD actions need to refresh the list / grid afterwards.
    // playerId lets delete allow removal of a type used only by the current player (V1 rule).
    const crudCtx = { selectedMonth: state.filters.selectedMonth, players: team.players, keyMetrics: team.keyMetrics, statuses, playerId: Number(playerId) };

    const vm = useMemo(
        () =>
            cellInfo
                ? buildCellViewModel(cellInfo, {
                      keyMetrics: team.keyMetrics,
                      acwrThresholdConfig: team.info.acwr_threshold_config,
                  })
                : null,
        [cellInfo, team]
    );

    const gpsChecklist = useMemo(() => {
        if (!cellInfo) return null;
        // Flag metadata is fetched lazily on modal open. Until this month's fetch resolves, show a
        // loading spinner on the info icon (handles fast cell-click → info-open before it arrives).
        const mKey = monthKeyOf(state.filters.selectedMonth);
        if (!state.flagMetaMonths[mKey]) return <GpsFlagChecklist loading />;
        const metaArr = (state.flagMetadata[dateStr] || []).filter((fm) => fm.player_id === player?.player_id);
        return <GpsFlagChecklist criteria={buildGPSCriteriaList(metaArr[0] || {})} />;
    }, [cellInfo, state.flagMetadata, state.flagMetaMonths, state.filters.selectedMonth, dateStr, player]);

    const selectedTagStatusId = cellInfo?.status?.status_id || cellInfo?.status?.id;
    const selectedTagBGColor = cellInfo?.status?.color;

    const close = () => {
        closeCell();
        setCustomInputVisible(false);
        setEditingStatus(null);
    };

    const selectStatus = (status) => {
        const isSelected = selectedTagStatusId === status.id;
        if (DATA_SOURCE === "mock") {
            actions.patchCellStatus(selectedCellKey, isSelected ? {} : { ...status, status_id: status.id });
            return;
        }
        const params = { uniqueKey: selectedCellKey, player_id: Number(playerId), date: dateStr, status_id: status.id };
        if (isSelected) actions.clearStatus(params);
        else actions.assignStatus(params);
    };

    const deleteCustStatus = (status) => {
        // status-types list carries only {id,name,color}; the server enforces ownership/usage
        // (404 not team-owned, 409 in use) and the action surfaces those via audioMessage.
        actions.deleteStatusType(status.id || status.status_id, crudCtx);
    };

    const onStatusEditClick = (e, status) => {
        e.stopPropagation();
        if (editingStatus !== null) {
            message.warning("Please complete the current edit before editing another status.");
            return;
        }
        setEditingStatus(status);
    };

    const onUpdaterConfirm = async (mode, params) => {
        if (mode === "create") {
            const ok = await actions.createStatusType({ name: params.name, color: params.color }, crudCtx);
            if (ok) setCustomInputVisible(false);
        } else if (mode === "edit") {
            const ok = await actions.updateStatusType(params.id, { name: params.name, color: params.color }, crudCtx);
            if (ok) setEditingStatus(null);
        }
    };

    return (
        <Modal
            title={`${player?.first_name ?? ""} ${player?.last_name ?? ""} - ${dateStr ?? ""}`}
            open={!!selectedCellKey}
            onCancel={close}
            footer={null}
            className="stv2-modal-root"
            rootClassName="stv2-modal-root"
        >
            <div>
                {cellInfo && Object.keys(cellInfo).length > 0 && vm && (
                    <div>
                        <AcwrTooltip rows={vm.acwrRows} showTrailingBreak />
                        <FlagTooltip
                            groups={vm.flagGroups}
                            isModalText
                            gpsChecklist={gpsChecklist}
                            uniqueKey={selectedCellKey}
                        />
                        <InjuryTooltip injuries={vm.injuries} uniqueKey={selectedCellKey} isModal />
                    </div>
                )}
                <span>
                    <strong>Select Status: </strong>
                </span>
                <br />
                <div className="stv2-modal-tag">
                    {statuses &&
                        statuses.length > 0 &&
                        statuses.map((status, i) =>
                            editingStatus !== null &&
                            (editingStatus.id === status.id || editingStatus.status_id === status.id) ? (
                                <StatusUpdater
                                    key={`${selectedCellKey}-modal-tag-${i}`}
                                    status={status}
                                    mode={"edit"}
                                    statuses={statuses}
                                    onConfirm={onUpdaterConfirm}
                                    onCancel={() => setEditingStatus(null)}
                                />
                            ) : (
                                <Tag
                                    className="stv2-modal-tag-individual"
                                    key={`${selectedCellKey}-modal-tag-${i}`}
                                    closable={canDelete(status)}
                                    style={{
                                        backgroundColor:
                                            status.id === selectedTagStatusId ? selectedTagBGColor : status.color,
                                        borderColor: status.id === selectedTagStatusId ? "black" : status.color,
                                        cursor: "pointer",
                                    }}
                                    onClick={() => selectStatus(status)}
                                    onClose={(e) => {
                                        e.preventDefault();
                                        deleteCustStatus(status);
                                    }}
                                >
                                    {status.name}
                                    {canEdit(status) && (
                                        <span
                                            onClick={(e) => onStatusEditClick(e, status)}
                                            style={{ marginBottom: 4, marginLeft: 4, cursor: "pointer" }}
                                        >
                                            <GoPencil />
                                        </span>
                                    )}
                                </Tag>
                            )
                        )}
                    {customInputVisible ? (
                        <StatusUpdater
                            mode={"create"}
                            statuses={statuses}
                            onConfirm={onUpdaterConfirm}
                            onCancel={() => setCustomInputVisible(false)}
                        />
                    ) : (
                        <Tag
                            className="stv2-modal-tag-individual stv2-custom-tag"
                            icon={<PlusOutlined />}
                            onClick={() => setCustomInputVisible(true)}
                        >
                            New Status
                        </Tag>
                    )}
                </div>
            </div>
        </Modal>
    );
};
